import React, { useRef } from 'react';

import type {
  MaintenanceListTableData,
  MaintenanceListTableDisplay,
  MaintenanceListTableGroupItem,
} from '@/components/sections/service/maintenance-list/table';
import { MaintenanceListTableFullScrollBar } from '@/components/sections/service/maintenance-list/table/full/scrollbar';
import { MaintenanceListTableFullTbody } from '@/components/sections/service/maintenance-list/table/full/tbody';
import { MaintenanceListTableFullTfoot } from '@/components/sections/service/maintenance-list/table/full/tfoot';
import { MaintenanceListTableFullThead } from '@/components/sections/service/maintenance-list/table/full/thead';
import { Box } from '@/components/shared/layout/box';
import { Grid } from '@/components/shared/layout/grid';
import { styled } from '@/stitches.config';
import type { IIcon } from '@/types/shared';

const TABLE_COLUMN_GAP = '$space-0-5';

const StyledTh = styled(Box, {
  padding: '$space-1 $space-4',
  minHeight: '$size-8',
  backgroundColor: '$primary100',
  color: '$white',
  textAlign: 'center',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  variants: {
    isLabel: {
      true: {
        position: 'sticky',
        top: 0,
        left: 0,
        zIndex: '$10',
      },
    },
    isTableValueCell: {
      true: {
        width: 'var(--table-cell-width)',
      },
    },
    isActive: {
      true: {
        backgroundColor: '$primary',
      },
    },
  },
});
const StyledTd = styled(Box, {
  position: 'relative',
  height: '100%',
  minHeight: '$size-12',
  display: 'flex',
  alignItems: 'center',
  px: '$space-4',
  py: '$space-3',
  color: '$gray900',
  '@sm': {
    px: '$space-4',
  },
  variants: {
    isLabel: {
      true: {
        textAlign: 'left',
        position: 'sticky',
        top: 0,
        left: 0,
        zIndex: '$10',
      },
    },
    isGroupTitle: {
      true: {
        bc: '$gray300',
      },
    },
    isTableTextLineCell: {
      true: {
        width: '100%',
      },
    },
    isTableValueCell: {
      true: {
        width: 'var(--table-cell-width)',
      },
    },
    align: {
      start: {
        justifyContent: 'flex-start',
      },
      center: {
        justifyContent: 'center',
        textAlign: 'center',
      },
      end: {
        justifyContent: 'flex-end',
      },
    },
    font: {
      bold: {
        fontFamily: '$bold',
      },
    },
    variant: {
      green: {
        color: '$green',
      },
    },
    isActive: {
      true: {
        '&::after': {
          content: '',
          position: 'absolute',
          inset: 0,
          backgroundColor: '$primary',
          opacity: 0.1,
        },
      },
    },
  },
});
const StyledGridLabelValue = styled(Box, {
  display: 'grid',
  gap: 2,
  gridTemplateColumns: 'var(--label-width) auto',
  variants: {
    isGroupTitleRow: {
      true: {
        position: 'relative',
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: '100%',
          height: 1,
          display: 'block',
          bc: '$gray400',
          pe: 'none',
          opacity: '0.5',
          zIndex: 11,
        },
      },
    },
    isBodyRow: {
      true: {
        bc: '$gray300',
      },
    },
  },
});
const StyledRowItemsWrapper = styled(Grid, {
  height: '100%',
  gridTemplateColumns: `repeat(var(--table-items-count), var(--table-cell-width))`,
  columnGap: TABLE_COLUMN_GAP,
});

export interface MaintenanceListTableFullData {
  headerLabelTitle?: string;
  headerLengthTitle?: string;
  tableCellWidth?: string;
  tableScrollBarSize?: string;
  generalIcon?: IIcon;
  generalTitle?: string;
}

interface MaintenanceListTableFullProps
  extends MaintenanceListTableFullData,
    MaintenanceListTableData {
  tableItems: MaintenanceListTableGroupItem[];
  tableDisplay?: MaintenanceListTableDisplay;
}

export const LABEL_WIDTH = {
  xs: 240,
  md: 312,
};

const MaintenanceListTableFull = ({
  vehicleType = 'lcv',
  maintenanceSchedules = [],
  selectedIndex,
  tableItems,
  headerLabelTitle = '',
  headerLengthTitle = '',
  tableCellWidth,
  tableScrollBarSize = '',
  generalIcon,
  generalTitle = '',
  priceData,
  tableDisplay,
}: MaintenanceListTableFullProps) => {
  const tableWrapperRef = useRef<HTMLDivElement>(null);

  const handleOnTableScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.target as HTMLDivElement;
    if (el && tableWrapperRef.current) {
      tableWrapperRef.current.style.setProperty(
        '--table-scroll',
        `${el.scrollLeft}px`
      );
    }
  };

  return (
    <Box
      position="relative"
      test={{
        dataTest: 'section_maintenanceList_table',
      }}
      ref={tableWrapperRef as React.Ref<HTMLDivElement>}
      paddingBottom="8"
      css={{
        '--table-cell-width': tableCellWidth,
        '--table-items-count': maintenanceSchedules.length,
        '--label-width': 'calc(var(--vw) - 184px)',
        '@xs': {
          '--label-width': `${LABEL_WIDTH.xs}px`,
        },
        '@md': {
          '--label-width': `${LABEL_WIDTH.md}px`,
        },
      }}
    >
      <MaintenanceListTableFullThead
        headerLabelTitle={headerLabelTitle}
        headerLengthTitle={headerLengthTitle}
        maintenanceSchedules={maintenanceSchedules}
        selectedIndex={selectedIndex}
      />
      <MaintenanceListTableFullTbody
        vehicleType={vehicleType}
        generalIcon={generalIcon}
        generalTitle={generalTitle}
        tableItems={tableItems}
        maintenanceSchedules={maintenanceSchedules}
        selectedIndex={selectedIndex}
      />
      <MaintenanceListTableFullTfoot
        costBeforeVatTitle={priceData?.costBeforeVatTitle}
        discountTitle={priceData?.discountTitle}
        totalCostWithVatTitle={priceData?.totalCostWithVatTitle}
        costBeforeVatIcon={priceData?.costBeforeVatIcon}
        discountIcon={priceData?.discountIcon}
        totalCostWithVatIcon={priceData?.totalCostWithVatIcon}
        maintenanceSchedules={maintenanceSchedules}
        selectedIndex={selectedIndex}
      />
      <MaintenanceListTableFullScrollBar
        thumbSize={tableScrollBarSize}
        tableItems={maintenanceSchedules}
        selectedIndex={selectedIndex}
        tableDisplay={tableDisplay}
        onScroll={handleOnTableScroll}
      />
    </Box>
  );
};
export {
  MaintenanceListTableFull,
  StyledGridLabelValue,
  StyledRowItemsWrapper,
  StyledTd,
  StyledTh,
};
